import { loadAllOrganSegmentations, OrganSegmentation } from './segmentationLoader';
import { addOrganSegmentationRepresentations, toggleSegmentationVisibility } from './segmentRenderer';
import {geometryLoader, getRenderingEngine} from "@cornerstonejs/core";
import {segmentation, drawing, Enums} from "@cornerstonejs/tools";
import {ContourData, IContourSet, Point3} from "@cornerstonejs/core/dist/esm/types";
import ContourSet from "@cornerstonejs/core/dist/esm/cache/classes/ContourSet";
import {Segmentation} from "@cornerstonejs/tools/dist/esm/types";

// Interface for segmentation filters
export interface SegmentationFilters {
  selectedOwners: Set<string>;   // Set of owner names to display (e.g., "GS", student names)
  selectedOrgans: Set<string>;   // Set of organ names to display
}

/**
 * SegmentationManager provides a high-level interface for working with organ segmentations
 * It orchestrates the loading, creation, and rendering of segmentations
 */
export class SegmentationManager {
  private viewportIds: {
    axial: string;
    sagittal: string;
    coronal: string;
    volume3D: string;
  };
  private organSegmentations: OrganSegmentation[] = [];
  private caseId?: string | number;
  
  // Store available owners and organs for filtering
  private availableOwners: Set<string> = new Set();
  private availableOrgans: Set<string> = new Set();
  
  // Current filters
  private filters: SegmentationFilters = {
    selectedOwners: new Set<string>(),
    selectedOrgans: new Set<string>()
  };
  
  // Tracking for hover state
  private highlightedSegmentation: OrganSegmentation | null = null;
  private labelElement: HTMLDivElement | null = null;
  private highlightedViewportId: string | null = null;
  private lastHighlightSettings: Map<string, any> = new Map();

  /**
   * Create a new SegmentationManager
   * @param viewportIds Object containing the IDs of all viewports
   * @param caseId The case ID to load segmentations for
   */
  constructor(
    viewportIds: {
      axial: string;
      sagittal: string;
      coronal: string;
      volume3D: string;
    },
    caseId?: string | number
  ) {
    this.viewportIds = viewportIds;
    this.caseId = caseId;
  }

  /**
   * Initialize the segmentation - load organ data and add representations
   */
  public async initialize(): Promise<void> {
    // First load all organ segmentations, passing the case ID
    this.organSegmentations = await loadAllOrganSegmentations(this.caseId);
    
    // Then add representations to the viewports
    await addOrganSegmentationRepresentations(this.viewportIds, this.organSegmentations);
    
    // Initialize the available owners and organs 
    this.updateAvailableFilters();
    
    // Don't apply default filters here - let the filter component handle initial state
  }
  
  /**
   * Update the sets of available owners and organs based on loaded segmentations
   */
  private updateAvailableFilters(): void {
    this.availableOwners.clear();
    this.availableOrgans.clear();
    
    this.organSegmentations.forEach(seg => {
      this.availableOwners.add(seg.owner);
      this.availableOrgans.add(seg.organName);
    });
  }
  
  /**
   * Apply the current filters to show/hide segmentations
   */
  private applyFilters(): void {
    // Update the visibility of each segmentation based on filters
    this.organSegmentations.forEach(seg => {
      const shouldBeVisible = 
        this.filters.selectedOwners.has(seg.owner) && 
        this.filters.selectedOrgans.has(seg.organName);
      // Only update if visibility is changing
      if (seg.visible !== shouldBeVisible) {
        seg.visible = shouldBeVisible;
        
        // Update visibility in the viewer
        toggleSegmentationVisibility(
          this.viewportIds,
          seg.segmentationId,
          shouldBeVisible
        );
      }
    });
  }

  /**
   * Get all the loaded organ segmentations
   */
  public getOrganSegmentations(): OrganSegmentation[] {
    return this.organSegmentations;
  }
  
  /**
   * Get available owners (GS and students)
   */
  public getAvailableOwners(): string[] {
    return Array.from(this.availableOwners);
  }
  
  /**
   * Get available organ names
   */
  public getAvailableOrgans(): string[] {
    return Array.from(this.availableOrgans);
  }
  
  /**
   * Toggle visibility for a specific owner's segmentations
   * @param owner Owner name to toggle
   * @param visible Whether to make the owner's segmentations visible
   */
  public toggleOwner(owner: string, visible: boolean): void {
    if (visible) {
      this.filters.selectedOwners.add(owner);
    } else {
      this.filters.selectedOwners.delete(owner);
    }
    
    this.applyFilters();
  }
  
  /**
   * Toggle visibility for a specific organ across all owners
   * @param organName Organ name to toggle
   * @param visible Whether to make the organ visible
   */
  public toggleOrgan(organName: string, visible: boolean): void {
    if (visible) {
      this.filters.selectedOrgans.add(organName);
    } else {
      this.filters.selectedOrgans.delete(organName);
    }
    
    this.applyFilters();
  }
  
  /**
   * Set the visibility for a specific owner+organ combination
   * @param owner Owner name
   * @param organName Organ name
   * @param visible Whether to make the segmentation visible
   */
  public setSegmentationVisibility(owner: string, organName: string, visible: boolean): void {
    const segmentation = this.organSegmentations.find(
      seg => seg.owner === owner && seg.organName === organName
    );
    
    if (segmentation) {
      segmentation.visible = visible;
      
      toggleSegmentationVisibility(
        this.viewportIds,
        segmentation.segmentationId,
        visible
      );
    }
  }
  
  /**
   * Get current filter state
   */
  public getFilters(): SegmentationFilters {
    return {
      selectedOwners: new Set(this.filters.selectedOwners),
      selectedOrgans: new Set(this.filters.selectedOrgans)
    };
  }
  
  /**
   * Set new filters and apply them
   * @param filters New filters to apply
   */
  public setFilters(filters: SegmentationFilters): void {
    this.filters = filters;
    this.applyFilters();
  }
  
  /**
   * Check if a world position is close to any visible segmentation
   * @param worldPos Array of world position coordinates
   * @param viewportId The ID of the viewport where the mouse is
   * @returns true if the cursor is close to a segmentation
   */
  public async checkSegmentationProximity(worldPos: number[] | null, viewportId: string): Promise<void> {
    // If there's no world position (cursor outside the viewport)
    if (!worldPos) {
      if (this.highlightedViewportId === viewportId) {
        this.removeHighlight(viewportId);
      }
      return;
    }

    // Get the rendering engine and viewport
    const renderingEngine = getRenderingEngine("myRenderingEngine");
    if (!renderingEngine) return;

    const viewport = renderingEngine.getViewport(viewportId);
    if (!viewport) return;


    let testSegmentation = null;
    let closestSeg = null;
    let minDist = Infinity;

    for (const seg of this.organSegmentations) {
      if (seg.owner == 'GS') continue;
      
      // Skip hidden/invisible segmentations
      if (!seg.visible) continue;

      const { distance } = await this.isPointNearSegmentation(worldPos, seg, viewportId);
      if (distance < minDist) {
        minDist = distance;
        closestSeg = seg;
      }
    }

    if (minDist <= 100) {
      testSegmentation = closestSeg;
    }


    if (testSegmentation) {
      // If we don't have a highlighted segmentation yet, or it's different from this one
      if (!this.highlightedSegmentation ||
          this.highlightedSegmentation.segmentationId !== testSegmentation.segmentationId ||
          this.highlightedViewportId !== viewportId) {

        console.log(`Found segmentation to highlight: ${testSegmentation.segmentationId}`);

        // Clean up any existing highlight in a different viewport
        if (this.highlightedSegmentation && this.highlightedViewportId &&
            this.highlightedViewportId !== viewportId) {
          this.removeHighlight(this.highlightedViewportId);
        }

        // Highlight the test segmentation
        this.highlightSegmentation(testSegmentation, worldPos, viewportId);
      } else {
        // If it's the same segmentation, just update the label position
        if (this.labelElement) {
          const canvasPos = viewport.worldToCanvas(worldPos as Point3);
          const element = document.getElementById(viewportId);
          if (element) {
            const rect = element.getBoundingClientRect();
            this.labelElement.style.left = `${rect.left + canvasPos[0]}px`;
            this.labelElement.style.top = `${rect.top + canvasPos[1] - 30}px`;
          }
        }
      }
    } else {
      // If we have a highlight in this viewport, remove it
      if (this.highlightedViewportId === viewportId) {
        this.removeHighlight(viewportId);
      }
    }
  }
  
  /**
   * Check if a point is near a specific segmentation
   * @param worldPos The world position to check
   * @param seg The segmentation to check against
   * @param viewportId The ID of the viewport
   * @returns An object with the distance if near, or null if not
   */
  private async isPointNearSegmentation(
      worldPos: number[],
      seg: OrganSegmentation,
      viewportId: string
  ): Promise<{ distance: number } | null> {
    try {

      // Load the geometry for this segmentation
      const geometry = await geometryLoader.loadGeometry(seg.geometryId);
      if (!geometry) return null;
      
      // Get the contour set data
      const contourSetData: IContourSet = geometry.data as IContourSet;
      if (!contourSetData || !contourSetData.contours) return null;
      
      // Find the minimum distance from the worldPos to any contour point on this slice
      let minDistance = Infinity;
      
      // Loop through all contours
      for (const contour of contourSetData.contours) {
        // Check if contour exists and has points
        if (!contour || !contour.points || contour.points.length === 0) continue;
        
        // Loop through all points in the contour to find the closest one
        for (const point of contour.points) {
          // Calculate distance from worldPos to this point (using only X and Y coordinates)
          const dx = point[0] - worldPos[0];
          const dy = point[1] - worldPos[1];
          const dz = point[2] - worldPos[2];
          const distanceToPoint = (dx * dx + dy * dy + dz*dz);
          
          if (distanceToPoint < minDistance) {
            minDistance = distanceToPoint;
          }
        }

      }

      // If we found a valid distance
      if (minDistance !== Infinity) {
        return { distance: minDistance };
      }
      
      // If no valid distance was found, return a very large distance
      return { distance: 1000 };
    } catch (error) {
      console.warn("Error checking point proximity:", error);
      return null;
    }
  }

  /**
   * Highlight a segmentation and show student information
   * @param segmentation The segmentation to highlight
   * @param worldPos The world position where to show the label
   * @param viewportId The ID of the viewport
   */
  private highlightSegmentation(
    segmentation: OrganSegmentation, 
    worldPos: number[], 
    viewportId: string
  ): void {
    // If we already have something highlighted in a different viewport,
    // make sure to remove that highlight first
    if (this.highlightedSegmentation && this.highlightedViewportId && 
        (this.highlightedSegmentation.segmentationId !== segmentation.segmentationId ||
         this.highlightedViewportId !== viewportId)) {
      this.restoreSegmentationVisibility(this.highlightedSegmentation, this.highlightedViewportId);
    }
    
    // Store the new highlighted segmentation and viewport
    this.highlightedSegmentation = segmentation;
    this.highlightedViewportId = viewportId;
    
    // Create or update the label element
    if (!this.labelElement) {
      this.labelElement = document.createElement('div');
      this.labelElement.style.position = 'absolute';
      this.labelElement.style.background = 'rgba(0, 0, 0, 0.7)';
      this.labelElement.style.color = 'white';
      this.labelElement.style.padding = '5px 10px';
      this.labelElement.style.borderRadius = '4px';
      this.labelElement.style.fontSize = '14px';
      this.labelElement.style.zIndex = '1000';
      this.labelElement.style.pointerEvents = 'none'; // Don't interfere with clicks
      document.body.appendChild(this.labelElement);
    }
    
    // Update the label content
    this.labelElement.textContent = `${segmentation.owner}`;
    
    // Position the label near the cursor
    const renderingEngine = getRenderingEngine("myRenderingEngine");
    if (!renderingEngine) return;
    
    const viewport = renderingEngine.getViewport(viewportId);
    if (!viewport) return;
    
    // Convert world position to canvas coordinates
    const canvasPos = viewport.worldToCanvas(worldPos as Point3);
    
    // Get the element's position on the page
    const element = document.getElementById(viewportId);
    if (!element) return;
    
    const rect = element.getBoundingClientRect();
    
    // Position the label above the cursor
    this.labelElement.style.left = `${rect.left + canvasPos[0]}px`;
    this.labelElement.style.top = `${rect.top + canvasPos[1] - 30}px`; // 30px above the cursor
    
    // Enhance the highlighted segmentation visually
    this.enhanceSegmentationVisibility(segmentation, viewportId);
  }
  
  /**
   * Remove the highlight from the currently highlighted segmentation
   * @param viewportId The viewport ID where the highlight should be removed
   */
  private removeHighlight(viewportId: string): void {
    if (this.highlightedSegmentation && this.highlightedViewportId) {
      // Restore the original appearance - only if the viewport matches
      if (!viewportId || viewportId === this.highlightedViewportId) {
        this.restoreSegmentationVisibility(this.highlightedSegmentation, this.highlightedViewportId);
        this.highlightedSegmentation = null;
        this.highlightedViewportId = null;
      }
    }
    
    // Remove the label element
    if (this.labelElement && this.labelElement.parentNode) {
      this.labelElement.parentNode.removeChild(this.labelElement);
      this.labelElement = null;
    }
  }
  
  /**
   * Enhance a segmentation's visibility when highlighted
   * @param seg The segmentation to enhance
   * @param viewportId The viewport ID where to apply the enhancement
   */
  private enhanceSegmentationVisibility(seg: OrganSegmentation, viewportId: string): void {
    try {
      // Store original settings for restoring later
      const segKey = `${seg.segmentationId}_${viewportId}`;
      

      
      // Check if we already have stored settings for this segmentation
      if (!this.lastHighlightSettings.has(segKey)) {
        // Store default values to restore later
        this.lastHighlightSettings.set(segKey, {
          style: {
            renderOutline: true,
            renderFill: false,
            activeSegmentOutlineWidthDelta: 2
          },
          viewportId: viewportId,
          color: [...seg.color, 255] // Store original color with alpha
        });
      }
      
      // Apply the highlight - make it simple and reliable
      try {
        // For axial view (contour representation)
        if (viewportId === this.viewportIds.axial) {
          segmentation.config.style.setStyle(
            {
              segmentationId: seg.segmentationId,
              viewportId: viewportId,
              type: Enums.SegmentationRepresentations.Contour,
            },
            {
              renderOutline: true,
              renderFill: true,
              fillAlpha: 0.4,
              outlineWidth: 3,
            }
          );
        } 
        // For sagittal/coronal views (labelmap representation)
        else if (viewportId === this.viewportIds.sagittal || viewportId === this.viewportIds.coronal) {
          segmentation.config.style.setStyle(
            {
              segmentationId: seg.segmentationId,
              viewportId: viewportId,
              type: Enums.SegmentationRepresentations.Labelmap,
            },
            {
              renderOutline: true,
              renderFill: true,
              fillAlpha: 0.4,
              outlineWidth: 3,
            }
          );
        }
      } catch (e) {
        console.error('Error applying style', e);
        
        // Fallback approach for all viewport types
        try {
          // Generic style application without specifying representation type
          segmentation.config.style.setStyle(
            {
              type: Enums.SegmentationRepresentations.Contour,
              segmentationId: seg.segmentationId,
              viewportId: viewportId
            },
            {
              renderOutline: true,
              renderFill: true,
              fillAlpha: 0.4,
              outlineWidth: 3,
            }
          );
        } catch (fallbackError) {
          console.error('Fallback style also failed', fallbackError);
        }
      }
      
      // Set color overlay with a brighter version of the original
      try {
        const originalColor = [...seg.color];
        const highlightColor = [
          Math.min(255, originalColor[0] + 70),
          Math.min(255, originalColor[1] + 70),
          Math.min(255, originalColor[2] + 70),
          255 // Full alpha
        ];
        
        segmentation.config.color.setSegmentIndexColor(
          viewportId,
          seg.segmentationId,
          1, // Segment index is usually 1
          highlightColor as any
        );
      } catch (colorError) {
        console.error('Error setting color', colorError);
      }

      // Trigger a render to update the display
      const renderingEngine = getRenderingEngine("myRenderingEngine");
      if (renderingEngine) {
        renderingEngine.render();
      }
      
    } catch (error) {
      console.warn(`Error enhancing visibility for ${seg.segmentationId}:`, error);
    }
  }
  
  /**
   * Restore a segmentation's original visibility after removing highlight
   * @param seg The segmentation to restore
   * @param viewportId The viewport ID where to restore the original appearance
   */
  private restoreSegmentationVisibility(seg: OrganSegmentation, viewportId: string): void {
    try {
      
      const segKey = `${seg.segmentationId}_${viewportId}`;
      
      // Get the stored original settings for this segmentation
      const originalSettings = this.lastHighlightSettings.get(segKey);
      
      try {
        // For axial view (contour representation)
        if (viewportId === this.viewportIds.axial) {
          segmentation.config.style.setStyle(
            {
              segmentationId: seg.segmentationId,
              viewportId: viewportId,
              type: Enums.SegmentationRepresentations.Contour,
            },
            {
              renderOutline: true,
              renderFill: false,
              outlineWidth: 1,
            }
          );
        } 
        // For sagittal/coronal views (labelmap representation)
        else if (viewportId === this.viewportIds.sagittal || viewportId === this.viewportIds.coronal) {
          segmentation.config.style.setStyle(
            {
              segmentationId: seg.segmentationId,
              viewportId: viewportId,
              type: Enums.SegmentationRepresentations.Labelmap,
            },
            {
              renderOutline: true,
              renderFill: false,
              outlineWidth: 1,
            }
          );
        }
      } catch (e) {
        console.error('Error restoring style', e);
        
        // Fallback approach without specifying representation type
        try {
          segmentation.config.style.setStyle(
            {
              type: Enums.SegmentationRepresentations.Contour,
              segmentationId: seg.segmentationId,
              viewportId: viewportId
            },
            {
              renderOutline: true,
              renderFill: false,
              outlineWidth: 1,
            }
          );
        } catch (fallbackError) {
          console.error('Fallback restore also failed', fallbackError);
        }
      }
      
      // Restore original color
      try {
        const originalColor = originalSettings?.color || [...seg.color, 255]; // Add alpha channel
        
        segmentation.config.color.setSegmentIndexColor(
          viewportId,
          seg.segmentationId,
          1, // Segment index is usually 1
          originalColor as any
        );
      } catch (colorError) {
        console.error('Error restoring color', colorError);
      }
      
      // Clean up stored settings
      if (originalSettings) {
        this.lastHighlightSettings.delete(segKey);
      }
      
      // Trigger a render to update the display
      const renderingEngine = getRenderingEngine("myRenderingEngine");
      if (renderingEngine) {
        renderingEngine.render();
      }
      
    } catch (error) {
      console.warn(`Error restoring visibility for ${seg.segmentationId} in viewport ${viewportId}:`, error);
    }
  }
}

/**
 * Create and initialize a SegmentationManager for the provided viewports
 * @param viewportIds Object containing the IDs of all viewports
 * @param caseId The case ID to load segmentations for
 * @returns The initialized SegmentationManager
 */
export async function createSegmentationManager(
  viewportIds: {
    axial: string;
    sagittal: string;
    coronal: string;
    volume3D: string;
  },
  caseId?: string | number
): Promise<SegmentationManager> {
  const manager = new SegmentationManager(viewportIds, caseId);
  await manager.initialize();
  return manager;
}